import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

interface SimpleMapViewProps {
  onBack: () => void;
  onAddressSelect: (address: {
    address_line_1: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  }) => void;
}

const SimpleMapView: React.FC<SimpleMapViewProps> = ({ onBack, onAddressSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedResult, setSelectedResult] = useState<any>(null);

  // Search for addresses using Nominatim (OpenStreetMap)
  const searchAddresses = async (query: string) => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=10&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching addresses:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search result selection
  const handleSearchResultSelect = (result: any) => {
    setSelectedResult(result);
    setSearchResults([]);
    setSearchQuery(result.display_name);
  };

  // Handle address confirmation
  const handleConfirmAddress = () => {
    if (!selectedResult) return;

    const address = selectedResult.address || {};
    const fullAddress = {
      address_line_1: selectedResult.display_name?.split(',')[0] || '',
      city: address.city || address.town || address.village || '',
      state: address.state || address.province || '',
      postal_code: address.postcode || '',
      country: address.country || '',
      latitude: parseFloat(selectedResult.lat),
      longitude: parseFloat(selectedResult.lon),
    };

    onAddressSelect(fullAddress);
  };

  // Search on Enter key
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchAddresses(searchQuery);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 2, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
      }}>
        <IconButton 
          onClick={onBack}
          sx={{ 
            mr: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Find Address
        </Typography>
      </Box>

      {/* Search bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <TextField
          fullWidth
          placeholder="Search for an address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSearching && <CircularProgress size={20} />}
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
        />
        
        {/* Search results */}
        {searchResults.length > 0 && (
          <Paper sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
            {searchResults.map((result, index) => (
              <Box
                key={index}
                onClick={() => handleSearchResultSelect(result)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderBottom: index < searchResults.length - 1 ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                  '&:hover': {
                    background: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {result.display_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {result.address?.country || 'Unknown Country'}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Map placeholder */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
        border: '2px dashed rgba(102, 126, 234, 0.3)',
        borderRadius: 2,
        m: 2,
        minHeight: '300px'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <LocationIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#667eea' }}>
            Interactive Map
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search for an address above to get started
          </Typography>
          <Alert severity="info" sx={{ borderRadius: 3, maxWidth: 400 }}>
            Use the search bar to find addresses worldwide. Click on any result to select it.
          </Alert>
        </Box>
      </Box>

      {/* Selected address and confirmation */}
      {selectedResult && (
        <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Selected Address
            </Typography>
          </Box>
          
          <Paper sx={{ p: 2, mb: 2, background: 'rgba(102, 126, 234, 0.05)' }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {selectedResult.display_name}
            </Typography>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedResult(null);
                setSearchQuery('');
              }}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Clear Selection
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAddress}
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Use This Address
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SimpleMapView;
